import { Button } from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useTranslation } from "react-i18next";
import { AsOfButton } from "./AsOfButton";
import { type AsOfSnapshotState, asOfNotice } from "./useAsOfSnapshot";

/**
 * The strip pinned under the page header while the page is in history mode (MDOT-24860): the
 * pinned date (which opens the picker to change it) on the left, "Return to today" on the right.
 *
 * It rides inside the header's own sticky container (the `bottomBar` slot), so it cannot drift
 * from a header whose height it does not know — and it is also where the snapshot's progress is
 * reported. That state used to be a floating pill, which could not work: the pill was the
 * impersonation banner's markup copied verbatim, down to `fixed top-2 left-1/2 z-[1500]` and its
 * colour, so operating as another user put two identical badges in one place. Then it lived on
 * the header's as-of button; now that button is live-view-only and this bar is the one sticky
 * surface the history view keeps, so the state belongs on it.
 */
export function AsOfBar(props: {
	/** Validated as-of date (`YYYY-MM-DD`) the page is pinned to. */
	asOf: string;
	setAsOf(asOf: string | null): void;
	/** Whether the pinned date's snapshot can be read yet. */
	snapshot: AsOfSnapshotState;
	/** Portfolio inception date (`YYYY-MM-DD` or full ISO timestamp) — earliest selectable day. */
	inceptionDate?: string;
}): React.JSX.Element {
	const { asOf, setAsOf, snapshot, inceptionDate } = props;
	const { t } = useTranslation();

	const notice = asOfNotice(asOf, snapshot);
	const failed = notice?.qualifier === "Failed";

	// Kept short: this sits inline on the bar, so the sentence goes in the tooltip.
	const state =
		notice == null
			? null
			: notice.qualifier === "Generating"
				? {
						text: t("PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATING_SHORT"),
						title: t("PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATING"),
						qualifier: notice.qualifier,
					}
				: {
						text: t(
							notice.reason === "timeout"
								? "PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATION_TIMEOUT_SHORT"
								: "PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATION_FAILED_SHORT",
						),
						title: t(
							notice.reason === "timeout"
								? "PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATION_TIMEOUT"
								: "PORTFOLIO_DETAILS.AS_OF_PICKER.GENERATION_FAILED",
						),
						qualifier: notice.qualifier,
					};

	return (
		<div
			className="flex items-center justify-between gap-4 px-6 py-2"
			style={{ background: themeCSSVars.palette_N700, borderBottom: `2px solid ${themeCSSVars.palette_N200}` }}
			data-qualifier="PortfolioDetails/AsOfBar"
		>
			<div className="flex items-center gap-3 min-w-0 text-white">
				<AsOfButton
					asOf={asOf}
					setAsOf={setAsOf}
					snapshot={snapshot}
					inceptionDate={inceptionDate}
					qualifier="PortfolioDetails/AsOfBar/Date"
				/>
				{state && (
					<span
						className="truncate"
						style={{ color: failed ? themeCSSVars.palette_D100 : themeCSSVars.palette_N0 }}
						title={state.title}
						data-qualifier={`PortfolioDetails/AsOfBar/${state.qualifier}`}
					>
						{state.text}
					</span>
				)}
			</div>
			<Button
				palette="primary"
				size="x-small"
				onClick={() => setAsOf(null)}
				data-qualifier="PortfolioDetails/AsOfBar/ReturnToToday"
			>
				{t("PORTFOLIO_DETAILS.AS_OF_PICKER.RETURN_TO_TODAY")}
			</Button>
		</div>
	);
}
